'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import cx from 'classnames';

// Utils
import { useReviews } from './reviews-context';

// UI
import { Button } from '@components/button';
import { IndividualReview } from './individual-review';
import { ReviewStats } from './review-stats';
import { SavvyReviewSummary } from './savvy-review-summary';
import { ReviewsSearchSort } from './review-search-sort';
import { Spinner, SpinnerSize } from '@components/spinner';
import { OtaReviewSummaries } from './ota-review-summaries';
import { ListingReviewSummarySource, ListingReviewSummariesListAllForListingQuery } from '@gql';
type Props = {
  averageRating: number;
  reviewsCount: number;
  airbnbUrl?: string;
  vrboUrl?: string;
  reviewSummary?: string;
};
type OtaReviewData = Extract<ListingReviewSummariesListAllForListingQuery['listingReviewSummariesListAllForListing'], {
  __typename: 'ListingReviewSummaryCollection';
}>['entries'][number];
const getOtaReviews = (listingReviewSummariesData: ReturnType<typeof useReviews>['listingReviewSummariesData']): OtaReviewData[] => {
  const collection = listingReviewSummariesData?.listingReviewSummariesListAllForListing;
  if (collection?.__typename !== 'ListingReviewSummaryCollection') {
    return [];
  }
  return collection.entries.filter(review => review.source === ListingReviewSummarySource.Airbnb || review.source === ListingReviewSummarySource.Vrbo).sort((a, b) => a.source === ListingReviewSummarySource.Airbnb ? -1 : b.source === ListingReviewSummarySource.Airbnb ? 1 : 0);
};
const SearchResultsMessage: React.FC<{
  totalSearchResults: number;
  searchValue: string;
}> = t0 => {
  const $ = _c(8);
  const {
    totalSearchResults,
    searchValue
  } = t0;
  let t1;
  if ($[0] !== totalSearchResults) {
    t1 = <span className="font-medium">{totalSearchResults}</span>;
    $[0] = totalSearchResults;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = totalSearchResults === 1 ? "review mentions" : "reviews mention";
  let t3;
  if ($[2] !== searchValue) {
    t3 = <span className="font-medium">{searchValue}</span>;
    $[2] = searchValue;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <p className="text-sm text-gray-main">{t1}{" "}{t2} "{t3}"</p>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
export const NoResultsFound: React.FC<{
  onClearSearch: () => void;
}> = t0 => {
  const $ = _c(4);
  const {
    onClearSearch
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="text-lg font-medium text-primary-main">No results found</p>;
    t2 = <p className="text-sm text-gray-main mt-2">No reviews match your search. Try adjusting your filters to see more reviews.</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onClearSearch) {
    t3 = <div className="flex flex-col items-start justify-start pt-4 pb-12 bg-white">{t1}{t2}<Button size="sm" onClick={onClearSearch} variant="outlined" color="gray" className="mt-8">Clear Search</Button></div>;
    $[2] = onClearSearch;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};
const ReviewsList: React.FC<{
  reviews: ReturnType<typeof useReviews>['allReviews'];
  isLoading: boolean;
  searchValue: string;
  onClearSearch: () => void;
}> = t0 => {
  const $ = _c(7);
  const {
    reviews,
    isLoading,
    searchValue,
    onClearSearch
  } = t0;
  if (isLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="flex justify-center items-start pt-4 h-full bg-white"><Spinner color="gray" size={SpinnerSize.SIZE_LARGE} /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (reviews.length === 0 && searchValue.trim()) {
    let t1;
    if ($[1] !== onClearSearch) {
      t1 = <NoResultsFound onClearSearch={onClearSearch} />;
      $[1] = onClearSearch;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== reviews) {
    t1 = reviews.slice(0, 3).map(_temp);
    $[3] = reviews;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="space-y-6">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const ShowAllReviewsButton: React.FC<{
  onClick: () => void;
}> = t0 => {
  const $ = _c(2);
  const {
    onClick
  } = t0;
  let t1;
  if ($[0] !== onClick) {
    t1 = <div className="flex justify-center py-4"><Button size="sm" onClick={onClick} variant="outlined" color="gray">Show All Reviews</Button></div>;
    $[0] = onClick;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const ReviewsSection: React.FC<Props> = t0 => {
  const $ = _c(70);
  const {
    averageRating,
    reviewsCount,
    airbnbUrl,
    vrboUrl,
    reviewSummary
  } = t0;
  const {
    allReviews,
    reviewsLoading,
    activateReviewsModal,
    deactivateReviewsModal,
    reviewsModalActive,
    searchValue,
    setSearchValue,
    sortValue,
    setSortValue,
    listingReviewsData,
    listingReviewSummariesData,
    listingReviewSummariesLoading
  } = useReviews();
  const collection = listingReviewsData?.listingReviewsListAllForListing;
  const totalSearchResults = collection?.__typename === "ListingReviewCollection" ? collection.meta.totalEntries || 0 : 0;
  let t1;
  if ($[0] !== allReviews.length || $[1] !== reviewsLoading || $[2] !== searchValue) {
    t1 = searchValue.trim() && allReviews.length > 0 && !reviewsLoading;
    $[0] = allReviews.length;
    $[1] = reviewsLoading;
    $[2] = searchValue;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const showSearchResultsMessage = t1;
  let t2;
  if ($[4] !== listingReviewSummariesData) {
    t2 = getOtaReviews(listingReviewSummariesData);
    $[4] = listingReviewSummariesData;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const otaReviews = t2;
  const handleToggleModal = reviewsModalActive ? deactivateReviewsModal : activateReviewsModal;
  let t3;
  if ($[6] !== searchValue) {
    t3 = searchValue.trim();
    $[6] = searchValue;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const hasSearchValue = t3.length > 0;
  const hasReviews = allReviews.length > 0;
  const showSearchSort = hasReviews || hasSearchValue;
  let t4;
  if ($[8] !== allReviews || $[9] !== reviewsLoading || $[10] !== searchValue || $[11] !== setSearchValue || $[12] !== showSearchSort) {
    t4 = showSearchSort && <ReviewsList reviews={allReviews} isLoading={reviewsLoading} searchValue={searchValue} onClearSearch={() => setSearchValue("")} />;
    $[8] = allReviews;
    $[9] = reviewsLoading;
    $[10] = searchValue;
    $[11] = setSearchValue;
    $[12] = showSearchSort;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== airbnbUrl || $[15] !== listingReviewSummariesLoading || $[16] !== otaReviews || $[17] !== vrboUrl) {
    t5 = <OtaReviewSummaries otaReviews={otaReviews} airbnbUrl={airbnbUrl} vrboUrl={vrboUrl} isLoading={listingReviewSummariesLoading} />;
    $[14] = airbnbUrl;
    $[15] = listingReviewSummariesLoading;
    $[16] = otaReviews;
    $[17] = vrboUrl;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== allReviews.length || $[20] !== handleToggleModal || $[21] !== reviewsLoading) {
    t6 = !reviewsLoading && allReviews.length > 3 && <ShowAllReviewsButton onClick={handleToggleModal} />;
    $[19] = allReviews.length;
    $[20] = handleToggleModal;
    $[21] = reviewsLoading;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t4 || $[24] !== t5 || $[25] !== t6) {
    t7 = <>{t4}{t5}{t6}</>;
    $[23] = t4;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const reviewsContent = t7;
  let t8;
  if ($[27] !== searchValue || $[28] !== setSearchValue || $[29] !== setSortValue || $[30] !== showSearchResultsMessage || $[31] !== showSearchSort || $[32] !== sortValue || $[33] !== totalSearchResults) {
    t8 = showSearchSort && <><div className="w-full"><ReviewsSearchSort searchValue={searchValue} sortValue={sortValue} onSearchChange={setSearchValue} onSortChange={setSortValue} /></div>{showSearchResultsMessage && <SearchResultsMessage totalSearchResults={totalSearchResults} searchValue={searchValue} />}</>;
    $[27] = searchValue;
    $[28] = setSearchValue;
    $[29] = setSortValue;
    $[30] = showSearchResultsMessage;
    $[31] = showSearchSort;
    $[32] = sortValue;
    $[33] = totalSearchResults;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  const searchSortSection = t8;
  let t9;
  if ($[35] !== averageRating || $[36] !== reviewsCount) {
    t9 = <div className="flex-shrink-0"><div className="sticky top-20"><ReviewStats averageRating={averageRating} reviewsCount={reviewsCount} /></div></div>;
    $[35] = averageRating;
    $[36] = reviewsCount;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== reviewSummary) {
    t10 = reviewSummary && <SavvyReviewSummary reviewSummary={reviewSummary} />;
    $[38] = reviewSummary;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== hasReviews) {
    t11 = cx("flex-1 space-y-6", {
      "min-h-[100px]": hasReviews
    });
    $[40] = hasReviews;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== reviewsContent || $[43] !== t11) {
    t12 = <div className={t11}>{reviewsContent}</div>;
    $[42] = reviewsContent;
    $[43] = t11;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== searchSortSection || $[46] !== t10 || $[47] !== t12) {
    t13 = <div className="px-2 flex-1 space-y-6">{t10}{searchSortSection}{t12}</div>;
    $[45] = searchSortSection;
    $[46] = t10;
    $[47] = t12;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== t13 || $[50] !== t9) {
    t14 = <div className="hidden lg:flex lg:space-x-8">{t9}{t13}</div>;
    $[49] = t13;
    $[50] = t9;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== averageRating || $[53] !== reviewsCount) {
    t15 = <ReviewStats averageRating={averageRating} reviewsCount={reviewsCount} />;
    $[52] = averageRating;
    $[53] = reviewsCount;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== reviewSummary) {
    t16 = reviewSummary && <SavvyReviewSummary reviewSummary={reviewSummary} />;
    $[55] = reviewSummary;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== hasReviews) {
    t17 = cx("space-y-6", {
      "min-h-[400px]": hasReviews
    });
    $[57] = hasReviews;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  let t18;
  if ($[59] !== reviewsContent || $[60] !== t17) {
    t18 = <div className={t17}>{reviewsContent}</div>;
    $[59] = reviewsContent;
    $[60] = t17;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  let t19;
  if ($[62] !== searchSortSection || $[63] !== t15 || $[64] !== t16 || $[65] !== t18) {
    t19 = <div className="lg:hidden space-y-6">{t15}{t16}{searchSortSection}{t18}</div>;
    $[62] = searchSortSection;
    $[63] = t15;
    $[64] = t16;
    $[65] = t18;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] !== t14 || $[68] !== t19) {
    t20 = <div className="space-y-8 pb-8 -mt-4 lg:pt-6">{t14}{t19}</div>;
    $[67] = t14;
    $[68] = t19;
    $[69] = t20;
  } else {
    t20 = $[69];
  }
  return t20;
};
function _temp(review) {
  return <IndividualReview key={review.id || `${review.authorName}-${review.date}`} review={review} />;
}